// hydrateOwn — the ONLY stores 07 hydrates itself (contract 07 §3, ledger A11). Legacy
// `service/StoreHydrator.ts` wrote six feature stores; after the inversion 07 keeps exactly two of
// its own writers — `hydrateSessionAccess` (v3 `StoreHydrator.ts:10-15`) and `hydrateAccountDetail`
// (`:26-29`) — and every other section flows through `hydratorRegistry`.
//
// The write target is INJECTED (the minimal `accountStore` write-surface) rather than reached via
// `useAccountStore()`: that keeps these pure and independently unit-testable, and it is the same
// inversion this module exists to enforce — 07's own writers do not hard-depend on a concrete store
// singleton either. The flow passes the real store in at boot.
import type {
  AccountDetail,
  AccountItem,
  UserAccessInfo,
  UserResponse,
} from '../models/account.types'
import type { MappedBootstrapResult, RbacSection } from '../models/bootstrap.types'

import { workspacePreferencesService } from './WorkspacePreferencesService'

/** The slice of `accountStore` these writers touch (contract §7 actions). */
export interface AccountHydrationTarget {
  setUser(user: UserResponse): void
  setAccounts(accounts: AccountItem[]): void
  setUserAccessInfo(info: UserAccessInfo): void
  setAccountDetail(detail: AccountDetail): void
  setRbac(rbac: RbacSection): void
}

/** `session_access` → 07's own `accountStore` (user + accounts + access info). Each field is
 *  written only when present, so a partial section never nulls out warm state (legacy parity). */
export function hydrateSessionAccess(
  target: AccountHydrationTarget,
  result: MappedBootstrapResult,
): void {
  if (result.user) target.setUser(result.user)
  if (result.accounts) target.setAccounts(result.accounts)
  if (result.userAccessInfo) target.setUserAccessInfo(result.userAccessInfo)
}

/** `account_detail` → 07's own `accountStore` (the RBAC/package source detail). */
export function hydrateAccountDetail(
  target: AccountHydrationTarget,
  result: MappedBootstrapResult,
): void {
  if (result.accountDetail) target.setAccountDetail(result.accountDetail)
}

/**
 * `rbac` → 07's own `accountStore` — the authoritative `isSysAdmin` + permission source
 * (BE-ACC-01). Same write-only-when-present rule as the writers above: a failed section must not
 * null out warm state and flap the admin shell mid-session (a `failed`/`cold` rbac maps to `null`
 * → skipped → the warm payload survives; only a `ready` section rewrites it). That is NOT a
 * client fallback — the bit is only ever a value the SERVER sent, it is never persisted (so a
 * refresh re-asks), and the backend enforces `admin_panel.*` independently. Cold boot has no
 * payload at all → `false`.
 */
export function hydrateRbac(target: AccountHydrationTarget, result: MappedBootstrapResult): void {
  if (result.rbac) target.setRbac(result.rbac)
}

/** The slice of `workspaceStore` the feature-flags writer touches (R2 target). */
export interface WorkspaceFlagsHydrationTarget {
  setFeatureFlags(enabled: string[], disabled?: string[]): void
}

/**
 * `feature_flags` → 07's own `workspaceStore.setFeatureFlags` (BE-ACC-02, ratified R2) on
 * workspace-scoped boots. The null-check is failed/cold protection, NOT an emptiness check: an
 * `empty` section maps to `[]` and MUST be written ("you have none" — every `checkFlag` gate
 * closes), while a `failed`/`cold` section maps to `null` and is skipped so warm flags survive
 * a degraded re-boot. Only `enabled_features` exists on the wire; `disabled` stays `[]`.
 */
export function hydrateFeatureFlags(
  target: WorkspaceFlagsHydrationTarget,
  result: MappedBootstrapResult,
): void {
  if (result.featureFlags) target.setFeatureFlags(result.featureFlags)
}

/** `workspace_core.preferences` → 07's generic, non-persisted preference cache. A failed/cold
 * section maps to `null` and preserves the last confirmed partition; explicit empty maps to `[]`
 * and clears it. Consumer keys and value DTOs remain opaque. */
export function hydrateWorkspacePreferences(
  accountId: number,
  workspaceId: number | string,
  result: MappedBootstrapResult,
): void {
  if (result.preferences === null) return
  workspacePreferencesService.hydrate({ accountId, workspaceId }, result.preferences)
}
